import { useState } from 'react'
import { Button, Form, InputNumber, Select, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leaveBalancesApi, leaveTypesApi, employeesApi } from '@/features/hr/api'
import type { LeaveBalance } from '@/shared/types/hr'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

export default function LeaveBalancesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [empFilter, setEmpFilter] = useState<string | undefined>()
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear())

  const canCreate = usePermission('leave.create')

  const { data, isLoading } = useQuery({
    queryKey: ['leave-balances', empFilter, yearFilter],
    queryFn: () => leaveBalancesApi.list({ employee_id: empFilter, year: yearFilter }),
  })

  const { data: employeesData } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeesApi.list({ page: 1, size: 100, is_active: true }),
  })

  const { data: leaveTypesData } = useQuery({
    queryKey: ['leave-types'],
    queryFn: () => leaveTypesApi.list(),
  })

  const empOptions = employeesData?.items.map((e) => ({ label: `${e.emp_code} — ${e.emp_name}`, value: e.id })) ?? []
  const ltOptions = leaveTypesData?.map((lt) => ({ label: lt.leave_name, value: lt.id })) ?? []
  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = new Date().getFullYear() - 2 + i
    return { label: String(y), value: y }
  })

  const openCreate = () => {
    form.resetFields()
    form.setFieldsValue({ year: yearFilter, opening: 0 })
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => leaveBalancesApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-balances'] })
      message.success('Leave balance created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
    { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'leave_type_name' },
    { title: 'Year', dataIndex: 'year', key: 'year', width: 80 },
    { title: 'Opening', dataIndex: 'opening', key: 'opening', width: 90 },
    { title: 'Availed', dataIndex: 'availed', key: 'availed', width: 90 },
    { title: 'Closing', dataIndex: 'closing', key: 'closing', width: 90 },
  ]

  return (
    <div>
      <PageHeader
        title="Leave Balances"
        subtitle="Track leave balances per employee per year"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Initialize Balance</Button>}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear showSearch optionFilterProp="label" placeholder="Filter by employee" options={empOptions} onChange={(v) => setEmpFilter(v)} style={{ width: 280 }} />
        <Select options={yearOptions} value={yearFilter} onChange={(v) => setYearFilter(v)} style={{ width: 100 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer title="Initialize Leave Balance" open={drawerOpen} onClose={() => setDrawerOpen(false)} onSubmit={onSubmit} loading={saveMutation.isPending} form={form}>
        <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={empOptions} placeholder="Select employee" /></Form.Item>
        <Form.Item name="leave_type_id" label="Leave Type" rules={[{ required: true }]}><Select options={ltOptions} placeholder="Select leave type" /></Form.Item>
        <Form.Item name="year" label="Year" rules={[{ required: true }]}><Select options={yearOptions} /></Form.Item>
        <Form.Item name="opening" label="Opening Balance" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} precision={1} /></Form.Item>
      </FormDrawer>
    </div>
  )
}
